import React, { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../api/api';

/**
 * BadgesPage - Steam風バッジシステム詳細ページ
 * カテゴリータブ、進捗バー、レア度表示
 */
const BadgesPage = () => {
  const [badgeData, setBadgeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  // ページタイトル設定
  useEffect(() => {
    document.title = 'バッジ | つむぎ';
  }, []);

  useEffect(() => {
    loadBadges();
  }, []);

  const loadBadges = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/badges');
      if (response.data.success) {
        setBadgeData(response.data.data);
      }
    } catch (error) {
      console.error('バッジ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <PageTitle
          title="あなたの記録"
          icon="🌱"
          backPath="/mypage"
        />
        <LoadingSpinner message="記録を読み込んでいます..." size="medium" />
      </AppLayout>
    );
  }

  if (!badgeData) {
    return (
      <AppLayout>
        <PageTitle
          title="あなたの記録"
          subtitle="データがありません"
          icon="🌱"
        />
      </AppLayout>
    );
  }

  const {
    stats,
    earnedBadges,
    lockedBadges,
    categoryStats,
    totalBadges,
    totalEarned,
    completionPercentage
  } = badgeData;

  // カテゴリーでフィルター
  const getFilteredBadges = (category) => {
    if (category === 'all') {
      return {
        earned: earnedBadges,
        locked: lockedBadges
      };
    }

    return {
      earned: earnedBadges.filter(b => b.category === category),
      locked: lockedBadges.filter(b => b.category === category)
    };
  };

  const filteredBadges = getFilteredBadges(activeCategory);

  return (
    <AppLayout>
      <PageTitle
        title="あなたの記録"
        subtitle={`${totalEarned} / ${totalBadges} 獲得 (${completionPercentage}%)`}
        icon="🎖️"
        backPath="/mypage"
        backLabel="マイページ"
      />

      <div className="tsumugi-container py-6">
        {/* 統計サマリー */}
        <div className="tsumugi-card">
          <div className="badge-stats-summary">
            <div className="badge-stats-summary__item">
              <div className="badge-stats-summary__number">{totalEarned}</div>
              <div className="badge-stats-summary__label">獲得済み</div>
            </div>
            <div className="badge-stats-summary__item">
              <div className="badge-stats-summary__number">{totalBadges - totalEarned}</div>
              <div className="badge-stats-summary__label">未獲得</div>
            </div>
            <div className="badge-stats-summary__item">
              <div className="badge-stats-summary__number">{completionPercentage}%</div>
              <div className="badge-stats-summary__label">達成率</div>
            </div>
          </div>

          {/* 全体進捗バー */}
          <div className="badge-overall-progress">
            <div className="badge-overall-progress__bar">
              {/* eslint-disable-next-line react/forbid-component-props */}
              <div
                className="badge-overall-progress__fill"
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* カテゴリータブ */}
        <div className="badge-category-tabs">
          {categoryStats.map(cat => (
            <button
              key={cat.id}
              className={`badge-category-tab ${activeCategory === cat.id ? 'badge-category-tab--active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              <span className="badge-category-tab__icon">{cat.icon}</span>
              <span className="badge-category-tab__name">{cat.name}</span>
              <span className="badge-category-tab__count">
                {cat.earned}/{cat.total}
              </span>
            </button>
          ))}
        </div>

        {/* 獲得済みバッジ */}
        {filteredBadges.earned.length > 0 && (
          <div className="tsumugi-card">
            <h3 className="tsumugi-card-title">🎉 獲得したバッジ</h3>
            <div className="badge-grid">
              {filteredBadges.earned.map((badge, index) => (
                <div key={index} className="badge-card badge-card--earned">
                  <div className="badge-card__icon">{badge.icon}</div>
                  <div className="badge-card__content">
                    <div className="badge-card__title">{badge.title}</div>
                    <div className="badge-card__description">{badge.description}</div>
                    <div className="badge-card__message">{badge.message}</div>
                    {badge.earnedAt && (
                      <div className="badge-card__earned-date">
                        {new Date(badge.earnedAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })} に獲得
                      </div>
                    )}
                    <div className="badge-card__rarity">
                      {badge.rarity}% のユーザーが獲得
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 未獲得バッジ */}
        {filteredBadges.locked.length > 0 && (
          <div className="tsumugi-card">
            <h3 className="tsumugi-card-title">🔒 未獲得のバッジ</h3>
            <div className="badge-grid">
              {filteredBadges.locked.map((badge, index) => {
                // 隠しバッジは「???」で表示
                const isHidden = badge.hidden;

                return (
                  <div key={index} className={`badge-card badge-card--locked ${isHidden ? 'badge-card--hidden' : ''}`}>
                    <div className="badge-card__icon">
                      {isHidden ? '🎁' : badge.icon}
                    </div>
                    <div className="badge-card__content">
                      <div className="badge-card__title">
                        {isHidden ? '???' : badge.title}
                      </div>
                      <div className="badge-card__description">
                        {isHidden ? '隠しバッジです' : badge.description}
                      </div>

                      {/* 進捗バー（隠しバッジ以外） */}
                      {!isHidden && (
                        <>
                          <div className="badge-card__progress">
                            <div className="badge-card__progress-bar">
                              {/* eslint-disable-next-line react/forbid-component-props */}
                              <div
                                className="badge-card__progress-fill"
                                style={{ width: `${badge.percentage}%` }}
                              ></div>
                            </div>
                            <div className="badge-card__progress-text">
                              {badge.progress} / {badge.target}
                            </div>
                          </div>

                          <div className="badge-card__rarity">
                            {badge.rarity}% のユーザーが獲得
                          </div>
                        </>
                      )}

                      {isHidden && (
                        <div className="badge-card__hint">
                          ???
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 記録の歩み */}
        {stats.firstRecordDate && (
          <div className="tsumugi-card">
            <h3 className="tsumugi-card-title">📆 記録の歩み</h3>
            <div className="badge-journey">
              <div className="badge-journey__item">
                <span className="badge-journey__label">最初の記録</span>
                <span className="badge-journey__value">
                  {new Date(stats.firstRecordDate).toLocaleDateString('ja-JP', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="badge-journey__item">
                <span className="badge-journey__label">累計記録日数</span>
                <span className="badge-journey__value">{stats.totalRecordDays}日</span>
              </div>
              <div className="badge-journey__item">
                <span className="badge-journey__label">最大連続記録</span>
                <span className="badge-journey__value">{stats.maxConsecutiveDays}日</span>
              </div>
              <div className="badge-journey__item">
                <span className="badge-journey__label">相談回数</span>
                <span className="badge-journey__value">{stats.totalConsultations}回</span>
              </div>
              <div className="badge-journey__item">
                <span className="badge-journey__label">つむぎと会話</span>
                <span className="badge-journey__value">{stats.totalChatSessions}回</span>
              </div>
            </div>
          </div>
        )}

        {/* バッジについての説明 */}
        <div className="tsumugi-card">
          <h3 className="tsumugi-card-title">💡 バッジについて</h3>
          <div className="mood-benefits">
            <div className="mood-benefits__item">
              ✅ <strong>あなたのペースで</strong>
              <div className="mood-benefits__example">
                → 記録できない日があっても大丈夫
              </div>
            </div>
            <div className="mood-benefits__item">
              ✅ <strong>小さな一歩を大切に</strong>
              <div className="mood-benefits__example">
                → どんな小さな記録も、大切な一歩です
              </div>
            </div>
            <div className="mood-benefits__item">
              ✅ <strong>続けた証が見える</strong>
              <div className="mood-benefits__example">
                → バッジは頑張った記録の証だよ
              </div>
            </div>
            <div className="mood-benefits__item">
              ✅ <strong>隠しバッジも探してみてね</strong>
              <div className="mood-benefits__example">
                → サプライズのバッジがあるかも？
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default BadgesPage;
